import { type FC, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
  Skeleton,
  CircularProgress,
  Checkbox,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import toast from 'react-hot-toast';
import type { VocabSuggestion } from '../../../types/api.types';
import {
  useGenerateVocabulary,
  useSaveVocabularyCards,
  useVocabularyWords,
} from '../../../hooks/useVocabulary';
import { useBackendSettings } from '../../../hooks/useBackendSettings';
import { settingsService } from '../../../services/settings.service';
import { VideoPlayerComprehensionSection } from './VideoPlayerComprehensionSection';

interface VideoPlayerVocabularyPanelProps {
  projectId: number;
  videoId?: string;
  episodeId?: number;
  /** Project language (e.g., "japanese", "spanish") used for frequency list selection and settings persistence. */
  projectLanguage: string;
  userLanguage: string;
  /** Whether the current episode has any transcript segments. Disables Generate when false. */
  hasTranscript: boolean;
  /** All segments for the current episode, used to label segment chips. */
  segments?: Array<{ id: number }>;
  /** Called when the user clicks a segment chip; parent should select that segment. */
  onSegmentClick?: (segmentId: number) => void;
}

/**
 * VideoPlayerVocabularyPanel — AI vocabulary card generation panel.
 *
 * Phase 3 — VOCAB-01/02/03/04
 *
 * States: idle, generating (skeletons), suggestions list, empty results, rate limit error,
 * generation error, no transcript.
 *
 * Frequency level persisted to user settings via settingsService.saveSettings (D-02).
 * Duplicate detection via useVocabularyWords against existing deck (D-03).
 */
export const VideoPlayerVocabularyPanel: FC<VideoPlayerVocabularyPanelProps> = ({
  projectId,
  videoId,
  episodeId,
  projectLanguage,
  hasTranscript,
  segments = [],
  onSegmentClick,
}) => {
  const { t } = useTranslation('player');
  const { settings } = useBackendSettings();

  // Map segment id → 1-based display index for the chip labels
  const segmentIndexMap = useMemo(() => {
    const m = new Map<number, number>();
    segments.forEach((s, i) => m.set(s.id, i + 1));
    return m;
  }, [segments]);

  // Restore saved frequency level from user settings; default 1000
  const resolveSavedLevel = (): 500 | 1000 | 3000 | 5000 => {
    const raw = parseInt(String(settings[`vocab_frequency_level_${projectLanguage}`] ?? '1000'), 10);
    return ([500, 1000, 3000, 5000] as number[]).includes(raw)
      ? (raw as 500 | 1000 | 3000 | 5000)
      : 1000;
  };

  const [frequencyLevel, setFrequencyLevel] = useState<500 | 1000 | 3000 | 5000>(1000);
  const [suggestions, setSuggestions] = useState<VocabSuggestion[]>([]);
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());
  const [hasGenerated, setHasGenerated] = useState(false);

  // localStorage cache key scoped to this video/episode
  const cacheKey = `vocab_suggestions_${videoId ?? `episode-${episodeId}`}`;

  // Restore cached suggestions on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw) as VocabSuggestion[];
        if (cached.length > 0) {
          setSuggestions(cached);
          setHasGenerated(true);
        }
      }
    } catch {
      // ignore malformed cache
    }
    // Only run on mount — cacheKey is stable for the episode's lifetime.
    // Do NOT clear on unmount: switching to the Transcript tab unmounts this panel,
    // and we want the suggestions to survive that round-trip.
    // Cache is cleared explicitly on regenerate (handleGenerate) and after save (handleSaveSelected).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync frequency level once settings load
  useEffect(() => {
    setFrequencyLevel(resolveSavedLevel());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, projectLanguage]);

  const {
    mutate: generate,
    isPending: isGenerating,
    data: generateData,
    error: generateError,
  } = useGenerateVocabulary();

  const { mutate: save, isPending: isSaving } = useSaveVocabularyCards();

  const { data: existingVocab = [] } = useVocabularyWords(projectId, projectLanguage);

  const existingWordSet = useMemo(
    () => new Set(existingVocab.map((w) => w.word.toLowerCase())),
    [existingVocab],
  );

  // Sync suggestions from mutation data and persist to localStorage
  useEffect(() => {
    if (generateData !== undefined) {
      setSuggestions(generateData);
      setHasGenerated(true);
      try {
        localStorage.setItem(cacheKey, JSON.stringify(generateData));
      } catch {
        // quota exceeded or private browsing — silently ignore
      }
    }
  }, [generateData, cacheKey]);

  const handleFrequencyChange = (e: SelectChangeEvent<number>) => {
    const level = e.target.value as 500 | 1000 | 3000 | 5000;
    setFrequencyLevel(level);
    // Fire-and-forget background save per D-02; no toast to avoid noise
    settingsService.saveSettings({ [`vocab_frequency_level_${projectLanguage}`]: String(level) });
  };

  const handleGenerate = () => {
    setSuggestions([]);
    setSelectedWords(new Set());
    setHasGenerated(false);
    localStorage.removeItem(cacheKey);
    generate({ projectId, ...(videoId ? { videoId } : { episodeId }), frequencyLevel });
  };

  const handleToggleWord = (word: string) => {
    setSelectedWords((prev) => {
      const next = new Set(prev);
      if (next.has(word)) {
        next.delete(word);
      } else {
        next.add(word);
      }
      return next;
    });
  };

  const handleSaveSelected = () => {
    const wordsToSave = suggestions
      .filter((s) => selectedWords.has(s.word))
      .map((s) => ({ word: s.word, translation: s.translation, segmentIds: s.segmentIds }));
    save(
      { projectId, words: wordsToSave },
      {
        onSuccess: () => {
          toast.success(t('vocabSaveSuccess', { count: wordsToSave.length }));
          setSuggestions([]);
          setSelectedWords(new Set());
          setHasGenerated(false);
          localStorage.removeItem(cacheKey);
        },
      },
    );
  };

  const isRateLimit =
    generateError != null && (generateError as any)?.response?.status === 429;

  const renderBody = () => {
    if (!hasTranscript) {
      return <Alert severity="info">{t('vocabNoTranscript')}</Alert>;
    }

    if (isGenerating) {
      return (
        <List disablePadding>
          {[0, 1, 2, 3].map((i) => (
            <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Skeleton variant="rectangular" width={20} height={20} />
              </ListItemIcon>
              <ListItemText
                primary={<Skeleton variant="text" width="60%" />}
                secondary={<Skeleton variant="text" width="40%" />}
              />
            </ListItem>
          ))}
        </List>
      );
    }

    if (isRateLimit) {
      return <Alert severity="warning">{t('vocabRateLimit')}</Alert>;
    }

    if (generateError) {
      return <Alert severity="error">{t('vocabGenerationError')}</Alert>;
    }

    if (suggestions.length > 0) {
      return (
        <List
          disablePadding
          aria-label={t('vocabSuggestionListAriaLabel')}
        >
          {suggestions.map((suggestion) => {
            const isDuplicate = existingWordSet.has(suggestion.word.toLowerCase());
            return (
              <ListItem key={suggestion.word} disableGutters sx={{ py: 0.25, alignItems: 'center' }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={!isDuplicate && selectedWords.has(suggestion.word)}
                    disabled={isDuplicate}
                    onChange={() => !isDuplicate && handleToggleWord(suggestion.word)}
                    inputProps={{ 'aria-label': suggestion.word }}
                  />
                </ListItemIcon>
                {/* Two-column table layout: word+translation | chips */}
                <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', minWidth: 0 }}>
                  <Box sx={{ flex: '0 0 40%', minWidth: 0, pr: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isDuplicate ? 400 : 700}
                      color={isDuplicate ? 'text.disabled' : 'text.primary'}
                      noWrap
                    >
                      {suggestion.word}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {isDuplicate ? t('vocabAlreadySaved') : suggestion.translation}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {suggestion.segmentIds.map((sid) => {
                      const label = segmentIndexMap.get(sid);
                      if (label === undefined) return null;
                      return (
                        <Chip
                          key={sid}
                          label={`Segment #${label}`}
                          size="small"
                          variant="outlined"
                          clickable={!!onSegmentClick}
                          onClick={onSegmentClick ? () => onSegmentClick(sid) : undefined}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </ListItem>
            );
          })}
        </List>
      );
    }

    if (hasGenerated && suggestions.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('vocabEmptyState')}
        </Typography>
      );
    }

    // Idle state
    return (
      <Typography variant="body2" color="text.secondary">
        {t('vocabEmptyState')}
      </Typography>
    );
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      aria-label={t('vocabTabAriaLabel')}
    >
      {/* Header Row — frequency selector + generate button */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flexWrap="nowrap"
        sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="vocab-freq-label">{t('vocabFrequencyLabel')}</InputLabel>
          <Select
            labelId="vocab-freq-label"
            value={frequencyLevel}
            label={t('vocabFrequencyLabel')}
            onChange={handleFrequencyChange}
            data-testid="vocab-frequency-select"
          >
            <MenuItem value={500}>{t('vocabFreq500')}</MenuItem>
            <MenuItem value={1000}>{t('vocabFreq1000')}</MenuItem>
            <MenuItem value={3000}>{t('vocabFreq3000')}</MenuItem>
            <MenuItem value={5000}>{t('vocabFreq5000')}</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          disabled={isGenerating || !hasTranscript}
          onClick={handleGenerate}
          sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
          data-testid="vocab-generate-button"
        >
          {isGenerating ? t('vocabGenerating') : t('vocabGenerateButton')}
        </Button>
      </Stack>

      {/* Scrollable body — renders based on current panel state */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {renderBody()}
      </Box>

      {/* Footer — Save Selected; only visible when suggestions are loaded */}
      {suggestions.length > 0 && (
        <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={selectedWords.size === 0 || isSaving}
            onClick={handleSaveSelected}
            data-testid="vocab-save-selected-button"
          >
            {isSaving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('vocabSaveSelected')
            )}
          </Button>
        </Box>
      )}

      {/* AI Comprehension Questions — Phase 4 */}
      <VideoPlayerComprehensionSection
        videoId={videoId}
        episodeId={episodeId}
        projectId={projectId}
        hasTranscript={hasTranscript}
      />
    </Box>
  );
};
